/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit, ViewChild, OnDestroy, ChangeDetectorRef, ElementRef, HostListener } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  CommonActions,
  ContactDetailsDialogConfig,
  contactDetailsDialogRoute,
  ContactFinderDialogConfig,
  contactFinderDialogRoute,
  ContactFinderListItem,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import { ContactType } from '@gh/builder-shared-data';
import {
  ActionFlow,
  AppRouter,
  compoundLogicalAction,
  exitAction,
  formStateSnapshot,
  getAllNewSelectedEntities,
  getRouteInitData,
  LogicalAction,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DialogService, FormMode, getControlValue, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { GridComponent } from '@gh/core-ui-grid';
import { filter$, map$, debounceTime$ } from '@gh/rx/operators';
import { Subscription } from 'rxjs';
import { RowNode } from 'ag-grid-community';
import { CostCenterDetailsBusinessService } from '../../resources/cost-center-details-business.service';
import {
  CostCenterDetails,
  CostCenterSupplier,
  costCenterSupplierFromFound,
} from '../../resources/cost-center-details.model';
import { CostCenterDetailsFormState } from './cost-center-details-form.service';
import { InputMatchingService } from '../../../shared/services/input-matching.service';

@Component({
  selector: 'gh-cost-center-details',
  template: require('./cost-center-details.component.html'),
  styles: [require('../../../details-tab-container.scss')],
  providers: [CostCenterDetailsFormState],
})
export class CostCenterDetailsComponent extends AbstractFormComponent<CostCenterDetails> implements OnInit, OnDestroy {

  matchingInput: string[] = [];
  private inputSubscription: Subscription;

  private ignoreInputValueChanges = false;

  @ViewChild(GridComponent, { static: false }) grid: GridComponent;
  @ViewChild('inputFieldWrapper', { static: true }) inputFieldWrapper: ElementRef;
  @ViewChild('numberField') fieldToFocus!: ElementRef;

  constructor(public form: CostCenterDetailsFormState,
              private route: ActivatedRoute,
              private businessService: CostCenterDetailsBusinessService,
              private navigationService: BuilderNavigationService,
              private dialogService: DialogService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private router: AppRouter,
              private inputMatchingService: InputMatchingService,
              private cdr: ChangeDetectorRef) {
    super();
  }

  get title(): string {
    return {
      [FormMode.CreateMode]: 'lbl_cc_create',
      [FormMode.EditMode]: 'lbl_cc_edit',
      [FormMode.ViewMode]: 'lbl_cc_view',
    }[this.form.mode];
  }

  get completeFlow(): ActionFlow<FormFlowState<CostCenterDetails>> {
    return this.pipelines.complete({
      execute: this.form.createMode ? this.confirmAndSaveAction : this.saveAction,
      finish: (state) => {
        const { value, form } = state;
        this.ignoreInputValueChanges = true;

        if (form.createMode) {
          this.router.switchTo(['/setup/estimating/cost-centers', value.id]);
        } else {
          form.build(state.value);
          setTimeout(() => this.ignoreInputValueChanges = false, 1000);
        }
      },
    });
  }

  get confirmAndSaveAction(): LogicalAction<FormFlowState<CostCenterDetails>> {
    return compoundLogicalAction('success', 'fail', {
      'start': this.saveAction.bindAs('save.success', 'save.fail'),
      'save.success': this.commonActions.confirmation(
        {
          ...yesNoConfirmation('msg_cc_add'),
          closeOnBackdropClick: false,
        },
        {
          yes: 'propagate.yes',
          no: 'propagate.no',
        }),
      'propagate.yes': this.propagateAction.bindAs('propagate.success', 'propagate.fail'),
      'propagate.no': exitAction('success'),
      'propagate.success': exitAction('success'),
      'propagate.fail': exitAction('success'),
      '*': exitAction('fail'),
    });
  }

  get propagateAction(): LogicalAction<FormFlowState<CostCenterDetails>> {
    return this.commonActions.execute((state: FormFlowState<CostCenterDetails>) => {
      const { value } = state;

      return this.businessService.propagateCostCenter(value.id);
    });
  }

  get saveAction(): LogicalAction<FormFlowState<CostCenterDetails>> {
    return this.commonActions.execute((state: FormFlowState<CostCenterDetails>) =>
      this.businessService.save(state.value));
  }

  ngOnInit(): void {
    const { costCenter } = getRouteInitData(this.route);

    this.form.build(costCenter);
    this.setupInputWatcher();

  }
  private setupInputWatcher(): void {
    const codeControl = this.form.group.get('code');
    if (codeControl) {
      this.inputSubscription = this.inputMatchingService.setupInputWatcher(
        codeControl.valueChanges,
        () => this.ignoreInputValueChanges,
        searchString => this.inputMatchingService.loadMatchingInput(
          searchString,
          this.businessService.getMatchingCostCenterCodes.bind(this.businessService),
          codes => (this.matchingInput = codes as string[]),
          this.cdr
        )
      );
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    this.inputMatchingService.clearMatchingInputOnClick(
      event,
      this.inputFieldWrapper.nativeElement,
      () => (this.matchingInput = []),
      this.cdr
    );
  }

  trackInput(code: string): string {
    return code;
  }

  ngOnDestroy(): void {
    this.inputMatchingService.unsubscribe(this.inputSubscription);
  }

  ngAfterViewInit(): void {
    if(this.form.mode === 1)this.fieldToFocus.nativeElement.focus();
  }

  canBeDeleted(supplier: CostCenterSupplier): boolean {
    return !this.isPreferred(supplier) || this.form.supplierCount === 1;
  }

  isPreferred(supplier: CostCenterSupplier): boolean {
    return supplier.contactId === this.form.preferredId;
  }

  goToAel(): void {
    this.navigationService.goToAel({
      entityId: this.form.id,
      entityType: 'CC',
      label: 'lbl_cost_center',
    });
  }

  onFindSuppliers(): void {
    this.dialogService.openWithInput(contactFinderDialogRoute, <ContactFinderDialogConfig>{
      type: ContactType.CreditorSupplier,
      preselected: this.form.contactIds,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((contacts: ContactFinderListItem[]) => {
        this.form.addSuppliers(contacts.map(costCenterSupplierFromFound));
        this.grid.api.setRowData(this.form.suppliersControl.controls);
      });
  }

  onAddSupplier(): void {
    this.dialogService.openWithInput(contactDetailsDialogRoute, <ContactDetailsDialogConfig>{
      contactType: ContactType.CreditorSupplier,
      parentTitle: this.title,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe(({ contactId, name }) => {
        this.form.addSuppliers([{ contactId, contactName: name }]);
        this.grid.api.setRowData(this.form.suppliersControl.controls);
      });
  }

  onAssignPreferred(supplier: CostCenterSupplier): void {
    this.form.assignPreferred(supplier);
  }

  onDeleteSupplier(node: RowNode): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_cc_supplier_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.grid.api.updateRowData({ remove: [node.data] });
        this.form.deleteSupplier(node.data.value);
      });
  }

  getSupplierId(supplierGroup: FormGroup): ID {
    return getControlValue(supplierGroup, 'contactId');
  }
}
